import ActivePage from "../activePage";

export default class TmkTaxSchedulePage extends ActivePage {
    constructor() {
        super();

        this.taxScheduleAddMode = false;
        this.suppressAgentPickerChange = false;
        this.beforeSubmit = this.beforeSubmit.bind(this);
        this.afterAggregateSave = this.afterAggregateSave.bind(this);
    }

    initializeDetailContentPage(detailContentPage) {
        this.taxScheduleAddMode = detailContentPage.addMode;

        detailContentPage.addModeOptions = $.extend({}, detailContentPage.addModeOptions, {
            beforeSubmit: this.beforeSubmit,
            afterSubmit: this.afterAggregateSave
        });

        detailContentPage.editModeOptions = $.extend({}, detailContentPage.editModeOptions, {
            beforeSubmit: this.beforeSubmit,
            afterSubmit: this.afterAggregateSave
        });

        super.initializeDetailContentPage(detailContentPage);
        this.initializeTaxScheduleMatrix();
    }

    beforeSubmit(onSubmit) {
        this.normalizeRequiredNumericInputs();
        this.serializeAgentColumns();
        this.setSkipRMSRenewalCostRecalculation(false);
        this.confirmRecalculation(onSubmit);
    }

    confirmRecalculation(onSubmit) {
        const matrix = this.getMatrix();
        const url = matrix.data("recalculation-count-url");

        if (this.taxScheduleAddMode || !url) {
            this.setSkipRMSRenewalCostRecalculation(false);
            onSubmit();
            return;
        }

        const form = this.getDetailContainer().find("form").first();
        if (form.length === 0) {
            this.setSkipRMSRenewalCostRecalculation(false);
            onSubmit();
            return;
        }

        cpiLoadingSpinner.show();
        pageHelper.postJson(url, pageHelper.formDataToJson(form))
            .done(result => {
                cpiLoadingSpinner.hide();

                const rawCount = Number(result ? result.recalculationCount || result.RecalculationCount : 0);
                const recalculationCount = Number.isFinite(rawCount) ? rawCount : 0;
                if (recalculationCount <= 0) {
                    this.setSkipRMSRenewalCostRecalculation(false);
                    onSubmit();
                    return;
                }

                const title = matrix.data("recalculation-confirm-title") || window.cpiBreadCrumbs.getTitle();
                const messageTemplate = matrix.data("recalculation-confirm-message") || "This change will recalculate Renewal Costs for {0} record(s). Do you want to continue?";
                cpiConfirm.open({
                    title,
                    message: messageTemplate.replace("{0}", recalculationCount.toString()),
                    cssClass: "confirm-three-action",
                    onConfirm: () => {
                        this.setSkipRMSRenewalCostRecalculation(false);
                        onSubmit();
                    },
                    onSecondary: () => {
                        this.setSkipRMSRenewalCostRecalculation(true);
                        onSubmit();
                    },
                    onCancel: () => {
                        this.setSkipRMSRenewalCostRecalculation(false);
                    },
                    buttons: {
                        action: {
                            class: "btn-primary",
                            label: matrix.data("recalculation-save-label") || "Save and Recalculate",
                            icon: "fa fa-sync"
                        },
                        secondary: {
                            class: "btn-secondary",
                            label: matrix.data("recalculation-skip-label") || "Save Without Recalculation",
                            icon: "fa fa-save"
                        },
                        close: {
                            class: "btn-secondary",
                            label: matrix.data("recalculation-cancel-label") || "Cancel",
                            icon: "fa fa-undo-alt"
                        }
                    }
                });
            })
            .fail(error => {
                cpiLoadingSpinner.hide();
                this.setSkipRMSRenewalCostRecalculation(false);
                pageHelper.showErrors(error);
            });
    }

    setSkipRMSRenewalCostRecalculation(skip) {
        this.getDetailContainer()
            .find("input[name='SkipRMSRenewalCostRecalculation']")
            .val(skip ? "true" : "false");
    }

    afterAggregateSave(result, options) {
        const keyId = this.getResultKeyId(result);
        const messages = this.getResultMessages(result);

        if (!keyId) {
            this.showServiceMessages(messages);
            return;
        }

        const refresh = this.taxScheduleAddMode
            ? this.afterInsert(keyId, options || {})
            : this.showDetails(keyId);

        if (refresh && refresh.always) {
            refresh.always(() => this.showServiceMessages(messages));
        } else if (refresh && refresh.then) {
            refresh.then(() => this.showServiceMessages(messages));
        } else {
            this.showServiceMessages(messages);
        }
    }

    searchResultDataBound = (e) => {
        const data = e.sender.dataSource.data();

        if (data.length === 0) {
            return;
        }

        const grid = e.sender.element;
        $.each(grid.find(".tmkTaxScheduleAgents-collapsible"), function () {
            const element = $(this);

            if (element.closest(".text-overflow").length === 0) {
                element.textOverflow();
            }
        });
    }

    availableAgentPickerData = () => {
        const widget = this.getAgentPickerWidget();

        return {
            keyId: this.getMatrix().data("key-id") || 0,
            pendingAgentIds: this.getVisibleAgentIds().join(","),
            text: widget && widget.input ? widget.input.val() : ""
        };
    }

    agentPickerChanged = event => {
        if (this.suppressAgentPickerChange) {
            return;
        }

        this.syncMatrixWithAgentPicker(event && event.sender ? event.sender : this.getAgentPickerWidget());
    }

    initializeTaxScheduleMatrix() {
        const matrix = this.getMatrix();

        if (matrix.length === 0) {
            return;
        }

        this.bindMatrixEvents(matrix);
        this.bindCurrencyTypeEvents(matrix);
        this.syncAgentPickerFromMatrix();
        this.refreshAgentPickerData();
        this.updateMatrixWidth(matrix);
        this.updatePreviews();
    }

    bindMatrixEvents(matrix) {
        matrix.off(".tmkTaxSchedule");

        matrix.on("click.tmkTaxSchedule", "[data-tax-action='remove-agent-column']", event => {
            event.preventDefault();
            this.removeAgentColumn(Number($(event.currentTarget).data("agent-id")));
        });

        matrix.on("input.tmkTaxSchedule change.tmkTaxSchedule", "input[data-tax-field], input[data-agent-honorary]", () => {
            this.markDetailDirty();
            this.updatePreviews();
        });

        this.bindNumericWidgets(matrix);
    }

    bindCurrencyTypeEvents(matrix) {
        const comboBox = this.getCurrencyTypeComboBox();
        const onChange = () => {
            matrix.data("exchange-rate", this.getSelectedCurrencyExchangeRate(comboBox));
            this.markDetailDirty();
            this.updatePreviews();
        };

        this.getDetailContainer()
            .find("input[name='CurrencyType']")
            .off(".tmkTaxScheduleCurrency")
            .on("change.tmkTaxScheduleCurrency", onChange);

        if (comboBox && !comboBox.element.data("tmk-tax-currency-bound")) {
            comboBox.bind("change", onChange);
            comboBox.element.data("tmk-tax-currency-bound", true);
        }
    }

    bindNumericWidgets(container) {
        const page = this;
        const onChange = event => {
            if (event && event.sender && event.sender.element) {
                page.normalizeRequiredNumericInput(event.sender.element);
            }

            page.markDetailDirty();
            page.updatePreviews();
        };

        container.find("input[data-tax-field], input[data-agent-honorary]").each(function () {
            const input = $(this);
            const widget = input.data("kendoNumericTextBox");

            if (!widget || input.data("tmk-tax-bound")) {
                return;
            }

            widget.bind("change", onChange);
            widget.bind("spin", onChange);
            input
                .off("blur.tmkTaxScheduleNumeric")
                .on("blur.tmkTaxScheduleNumeric", () => {
                    if (page.normalizeRequiredNumericInput(input)) {
                        page.markDetailDirty();
                        page.updatePreviews();
                    }
                });
            input.data("tmk-tax-bound", true);
        });
    }

    normalizeRequiredNumericInputs(container) {
        const currentContainer = container && container.length ? container : this.getMatrix();

        currentContainer.find("input[data-tax-field], input[data-agent-honorary]").each((_, element) => {
            this.normalizeRequiredNumericInput($(element));
        });
    }

    normalizeRequiredNumericInput(input) {
        if (!input || input.length === 0) {
            return false;
        }

        const widget = input.data("kendoNumericTextBox");
        if (!widget) {
            return false;
        }

        const displayValue = widget.input && widget.input.length ? widget.input.val() : input.val();
        const value = widget.value();
        const isBlank = displayValue === null || displayValue === undefined || displayValue.toString().trim() === "";
        const isInvalid = value === null || value === undefined || Number.isNaN(Number(value));

        if (!isBlank && !isInvalid) {
            return false;
        }

        widget.value(0);
        return true;
    }

    syncMatrixWithAgentPicker(widget) {
        if (!widget) {
            return;
        }

        const selectedIds = new Set(this.getAgentPickerValues(widget));
        let visibleAgentIds = this.getVisibleAgentIds();
        let changed = false;
        let firstAddedAgentId = null;

        visibleAgentIds.forEach(agentId => {
            if (!selectedIds.has(agentId)) {
                this.removeAgentColumn(agentId, {
                    markDirty: false,
                    syncPicker: false,
                    updateMatrixWidth: false,
                    updatePreviews: false
                });
                changed = true;
            }
        });

        visibleAgentIds = new Set(this.getVisibleAgentIds());
        const selectedAgents = this.getSelectedAgentDataItems(widget);

        selectedAgents.forEach(agent => {
            const agentId = Number(agent.AgentID);

            if (!agentId || visibleAgentIds.has(agentId)) {
                return;
            }

            this.addAgentColumn({
                AgentID: agentId,
                AgentCode: agent.AgentCode || "",
                AgentName: agent.AgentName || "",
                IsActive: agent.IsActive !== false,
                BasisAgentHonorary: 0,
                AddAgentHonorary: 0
            });

            visibleAgentIds.add(agentId);
            firstAddedAgentId = firstAddedAgentId || agentId;
            changed = true;
        });

        this.syncAgentPickerFromMatrix({ widget });
        this.refreshAgentPickerData(widget);

        if (!changed) {
            return;
        }

        this.updateMatrixWidth();
        this.updatePreviews();
        this.markDetailDirty();

        if (firstAddedAgentId) {
            this.scrollToAgent(firstAddedAgentId);
            this.focusAgentBasisInput(firstAddedAgentId);
        }
    }

    addAgentColumn(agent) {
        const matrix = this.getMatrix();
        const agentId = Number(agent.AgentID);
        const encodedCode = kendo.htmlEncode(agent.AgentCode || "");
        const encodedName = kendo.htmlEncode(agent.AgentName || "");
        const inactiveTitle = matrix.data("inactive-agent-title") || "Inactive Agent";
        const isActive = agent.IsActive !== false;
        const displayCode = `${encodedCode}${isActive ? "" : " *"}`;
        const title = isActive ? encodedName : kendo.htmlEncode(inactiveTitle);
        const removeTitle = kendo.htmlEncode(this.getRemoveAgentTitle(matrix));
        const removeButton = this.isReadOnly(matrix) ? "" :
            `<button type="button" class="tmk-agent-remove" data-tax-action="remove-agent-column" data-agent-id="${agentId}" title="${removeTitle}" aria-label="${removeTitle}">
                <span class="k-icon fal fa-times" aria-hidden="true"></span>
            </button>`;

        matrix.find("thead tr").append(
            `<th class="tmk-agent-header tmk-agent-column" data-agent-id="${agentId}" data-agent-code="${encodedCode}" data-agent-name="${encodedName}" data-is-active="${isActive}">
                <div class="tmk-agent-header-content">
                    <div class="tmk-agent-title">
                        <span title="${title}">${displayCode}</span>
                        <small>${encodedName}</small>
                    </div>
                    ${removeButton}
                </div>
            </th>`
        );

        matrix.find("tr[data-tax-row='basis']").append(
            `<td data-agent-id="${agentId}">
                <input type="text" name="AgentBasisHonorary_${agentId}" data-agent-honorary="basis" data-agent-id="${agentId}" style="width: 100%" />
            </td>`
        );

        matrix.find("tr[data-tax-row='additional']").append(
            `<td data-agent-id="${agentId}">
                <input type="text" name="AgentAddHonorary_${agentId}" data-agent-honorary="add" data-agent-id="${agentId}" style="width: 100%" />
            </td>`
        );

        matrix.find("tr[data-tax-row='preview-basis']").append(
            `<td class="tmk-preview-cell" data-preview-row="basis" data-preview-agent-id="${agentId}"></td>`
        );

        matrix.find("tr[data-tax-row='preview-additional']").append(
            `<td class="tmk-preview-cell" data-preview-row="additional" data-preview-agent-id="${agentId}"></td>`
        );

        const newInputs = matrix.find(`input[data-agent-id='${agentId}']`);
        newInputs.kendoNumericTextBox({
            format: "n4",
            decimals: 4,
            min: 0,
            value: 0
        });

        this.updateMatrixWidth(matrix);
        this.bindNumericWidgets(matrix);
    }

    removeAgentColumn(agentId, options = {}) {
        if (!agentId) {
            return;
        }

        const matrix = this.getMatrix();
        const header = matrix.find(`.tmk-agent-column[data-agent-id='${agentId}']`);

        if (header.length === 0) {
            return;
        }

        const cells = matrix.find(`tbody td[data-agent-id='${agentId}'], .tmk-preview-cell[data-preview-agent-id='${agentId}']`);
        cells.each(function () {
            kendo.destroy(this);
        });

        header.remove();
        cells.remove();

        if (options.syncPicker !== false) {
            this.syncAgentPickerFromMatrix();
        }

        if (options.updateMatrixWidth !== false) {
            this.updateMatrixWidth(matrix);
        }

        if (options.updatePreviews !== false) {
            this.updatePreviews();
        }

        if (options.markDirty !== false) {
            this.markDetailDirty();
        }
    }

    syncAgentPickerFromMatrix(options = {}) {
        const widget = options.widget || this.getAgentPickerWidget();

        if (!widget) {
            return;
        }

        const visibleAgents = this.getVisibleAgentData();
        const visibleAgentIds = visibleAgents.map(agent => agent.AgentID);

        this.suppressAgentPickerChange = true;
        try {
            this.ensureAgentPickerDataItems(widget, visibleAgents);
            widget.value(visibleAgentIds);
        } finally {
            this.suppressAgentPickerChange = false;
        }

        this.resetAgentPickerFloatLabel(widget);
    }

    refreshAgentPickerData(widget = this.getAgentPickerWidget()) {
        if (!widget || !widget.dataSource || !widget.dataSource.read) {
            return;
        }

        const request = widget.dataSource.read();
        this.resetAgentPickerFloatLabel(widget);

        if (request && request.always) {
            request.always(() => this.resetAgentPickerFloatLabel(widget));
        }
    }

    resetAgentPickerFloatLabel(widget = this.getAgentPickerWidget()) {
        if (!widget || !widget.element || !widget.element.resetFloatLabel) {
            return;
        }

        widget.element.resetFloatLabel();
    }

    updateMatrixWidth(matrix) {
        const currentMatrix = matrix && matrix.length ? matrix : this.getMatrix();

        if (currentMatrix.length === 0 || !currentMatrix[0]) {
            return;
        }

        const fixedColumnWidth = 910;
        const agentColumnWidth = 160;
        const agentCount = currentMatrix.find(".tmk-agent-column").length;

        currentMatrix[0].style.setProperty("--tmk-tax-min-width", `${fixedColumnWidth + (agentCount * agentColumnWidth)}px`);
    }

    serializeAgentColumns() {
        const columns = this.collectAgentColumns();
        this.getDetailContainer()
            .find("input[name='AgentColumnsJson']")
            .val(JSON.stringify(columns));
    }

    collectAgentColumns() {
        const matrix = this.getMatrix();

        return matrix.find(".tmk-agent-column").map((_, element) => {
            const header = $(element);
            const agentId = Number(header.attr("data-agent-id"));

            return {
                AgentID: agentId,
                AgentCode: header.attr("data-agent-code") || "",
                AgentName: header.attr("data-agent-name") || "",
                IsActive: header.attr("data-is-active") !== "false",
                BasisAgentHonorary: this.numberOrZero(this.getAgentValue("basis", agentId)),
                AddAgentHonorary: this.numberOrZero(this.getAgentValue("add", agentId))
            };
        }).get();
    }

    updatePreviews() {
        const matrix = this.getMatrix();

        if (matrix.length === 0) {
            return;
        }

        const inputs = this.getCalculationInputs(matrix);
        const agentIds = [0].concat(this.getVisibleAgentIds());

        agentIds.forEach(agentId => {
            const agentBasis = agentId === 0 ? 0 : this.getAgentValue("basis", agentId);
            const agentAdd = agentId === 0 ? 0 : this.getAgentValue("add", agentId);
            const basisPreview = this.calculateBasisPreview(inputs, agentBasis);
            const additionalPreview = this.calculateAdditionalClassPreview(inputs, agentAdd);

            this.writePreview("basis", agentId, basisPreview);
            this.writePreview("additional", agentId, additionalPreview);
        });
    }

    getCalculationInputs(matrix) {
        return {
            hasCurrencyType: this.hasCurrencyType(),
            exchangeRate: this.getEffectiveExchangeRate(matrix.data("exchange-rate")),
            basisNumOfClasses: this.getTaxFieldValue(matrix, "basis-num-classes"),
            basisRenewalAmount: this.getTaxFieldValue(matrix, "basis-renewal-amount"),
            basisHonorary: this.getTaxFieldValue(matrix, "basis-honorary"),
            addRenewalAmount: this.getTaxFieldValue(matrix, "add-renewal-amount"),
            addHonorary: this.getTaxFieldValue(matrix, "add-honorary")
        };
    }

    calculateBasisPreview(inputs, agentBasis) {
        if (!this.hasRequiredCalculationInputs(inputs) || agentBasis === null) {
            return null;
        }

        if (inputs.basisNumOfClasses === 0) {
            return 0;
        }

        const baseRenewalCost = this.roundMoney(inputs.basisRenewalAmount * inputs.exchangeRate);

        return this.roundMoney(baseRenewalCost + inputs.basisHonorary + agentBasis);
    }

    calculateAdditionalClassPreview(inputs, agentAdd) {
        if (!this.hasRequiredCalculationInputs(inputs) || agentAdd === null) {
            return null;
        }

        if (inputs.basisNumOfClasses === 0) {
            return 0;
        }

        return this.roundMoney(
            inputs.addRenewalAmount * inputs.exchangeRate +
            inputs.addHonorary * inputs.exchangeRate +
            agentAdd * inputs.exchangeRate
        );
    }

    hasRequiredCalculationInputs(inputs) {
        return inputs.hasCurrencyType &&
            inputs.basisNumOfClasses !== null &&
            inputs.basisRenewalAmount !== null &&
            inputs.basisHonorary !== null &&
            inputs.addRenewalAmount !== null &&
            inputs.addHonorary !== null;
    }

    getEffectiveExchangeRate(value) {
        const rate = Number(value);
        return !Number.isFinite(rate) || rate === 0 ? 1 : rate;
    }

    getTaxFieldValue(matrix, field) {
        return this.readNumericValue(matrix.find(`input[data-tax-field='${field}']`).first());
    }

    hasCurrencyType() {
        const comboBox = this.getCurrencyTypeComboBox();
        return comboBox ? !!comboBox.value() : !!this.getDetailContainer().find("input[name='CurrencyType']").val();
    }

    getCurrencyTypeComboBox() {
        const input = this.getDetailContainer().find("input[name='CurrencyType']").first();
        return input.data("kendoComboBox") || input.data("kendoMultiColumnComboBox");
    }

    getSelectedCurrencyExchangeRate(comboBox) {
        if (!comboBox || !comboBox.value()) {
            return null;
        }

        const currencyCode = this.normalizeCurrencyCode(comboBox.value());
        const defaultCurrency = this.getDefaultCurrency();

        if (currencyCode && defaultCurrency && currencyCode === defaultCurrency) {
            return 1;
        }

        const dataItem = this.getSelectedCurrencyDataItem(comboBox);
        const exchangeRate = this.getExchangeRateForDefaultCurrency(dataItem, defaultCurrency);

        return Number.isFinite(exchangeRate) ? exchangeRate : null;
    }

    getDefaultCurrency() {
        return this.normalizeCurrencyCode(this.getMatrix().data("default-currency") || "USD");
    }

    getExchangeRateForDefaultCurrency(dataItem, defaultCurrency) {
        if (!dataItem) {
            return null;
        }

        const rateField = {
            EUR: "EUR_ExRate",
            GBP: "GBP_ExRate",
            DKK: "DKK_ExRate"
        }[defaultCurrency] || "USD_ExRate";

        const baseCurrencyRateValue = dataItem[rateField];
        const baseCurrencyRate = baseCurrencyRateValue === null || baseCurrencyRateValue === undefined || baseCurrencyRateValue === ""
            ? null
            : Number(baseCurrencyRateValue);

        if (Number.isFinite(baseCurrencyRate)) {
            return baseCurrencyRate;
        }

        return Number(dataItem.ExchangeRate);
    }

    normalizeCurrencyCode(value) {
        return (value || "").toString().trim().toUpperCase();
    }

    getSelectedCurrencyDataItem(comboBox) {
        if (comboBox.selectedIndex >= 0 && comboBox.dataItem()) {
            return comboBox.dataItem();
        }

        const dataValueField = comboBox.options.dataValueField;
        const dataValue = comboBox.value();
        const data = comboBox.dataSource && comboBox.dataSource._data ? comboBox.dataSource._data : [];

        for (let i = 0; i < data.length; i++) {
            if (data[i][dataValueField] == dataValue) {
                return data[i];
            }
        }

        return null;
    }

    getAgentValue(type, agentId) {
        return this.readNumericValue(
            this.getMatrix().find(`input[data-agent-honorary='${type}'][data-agent-id='${agentId}']`).first()
        );
    }

    readNumericValue(input) {
        if (!input || input.length === 0) {
            return null;
        }

        const widget = input.data("kendoNumericTextBox");
        const value = widget ? widget.value() : kendo.parseFloat(input.val());

        return value === null || value === undefined || Number.isNaN(Number(value))
            ? null
            : Number(value);
    }

    writePreview(row, agentId, value) {
        const cell = this.getMatrix().find(`[data-preview-row='${row}'][data-preview-agent-id='${agentId}']`);
        cell.text(value === null ? "" : kendo.toString(value, "n2"));
    }

    roundMoney(value) {
        const factor = 10000;
        return Math.sign(value) * Math.round(Math.abs(value) * factor + Number.EPSILON) / factor;
    }

    numberOrZero(value) {
        return value === null ? 0 : value;
    }

    getVisibleAgentIds() {
        return this.getMatrix().find(".tmk-agent-column").map((_, element) => {
            return Number($(element).attr("data-agent-id"));
        }).get().filter(agentId => agentId > 0);
    }

    getVisibleAgentData() {
        return this.getMatrix().find(".tmk-agent-column").map((_, element) => {
            const header = $(element);

            return {
                AgentID: Number(header.attr("data-agent-id")),
                AgentCode: header.attr("data-agent-code") || "",
                AgentName: header.attr("data-agent-name") || "",
                IsActive: header.attr("data-is-active") !== "false"
            };
        }).get().filter(agent => agent.AgentID > 0);
    }

    getAgentPickerValues(widget) {
        if (!widget || !widget.value) {
            return [];
        }

        return widget.value()
            .map(value => Number(value))
            .filter(agentId => agentId > 0);
    }

    getSelectedAgentDataItems(widget) {
        const itemsById = new Map();
        const selectedDataItems = this.toArray(widget && widget.dataItems ? widget.dataItems() : []);
        const sourceDataItems = this.toArray(widget && widget.dataSource ? widget.dataSource.data() : []);

        selectedDataItems.concat(sourceDataItems).forEach(item => {
            const agent = this.toPlainAgent(item);
            if (agent && agent.AgentID > 0) {
                itemsById.set(agent.AgentID, agent);
            }
        });

        return this.getAgentPickerValues(widget)
            .map(agentId => itemsById.get(agentId))
            .filter(agent => !!agent);
    }

    ensureAgentPickerDataItems(widget, agents) {
        if (!widget || !widget.dataSource) {
            return;
        }

        const dataSource = widget.dataSource;
        const existingIds = new Set(this.toArray(dataSource.data())
            .map(item => Number(item.AgentID))
            .filter(agentId => agentId > 0));

        agents.forEach(agent => {
            if (!existingIds.has(agent.AgentID)) {
                dataSource.add(agent);
                existingIds.add(agent.AgentID);
            }
        });
    }

    toArray(items) {
        if (!items) {
            return [];
        }

        if (items.toJSON) {
            return items.toJSON();
        }

        return Array.prototype.slice.call(items);
    }

    toPlainAgent(item) {
        if (!item) {
            return null;
        }

        const agent = item.toJSON ? item.toJSON() : item;
        const agentId = Number(agent.AgentID);

        if (!agentId) {
            return null;
        }

        return {
            AgentID: agentId,
            AgentCode: agent.AgentCode || "",
            AgentName: agent.AgentName || "",
            IsActive: agent.IsActive !== false
        };
    }

    getRemoveAgentTitle(matrix) {
        const agentLabel = (matrix && matrix.data("agent-label")) || "Agent";
        return `Remove ${agentLabel}`;
    }

    isReadOnly(matrix) {
        const value = (matrix && matrix.length ? matrix : this.getMatrix()).data("read-only");
        return value === true || value === "true";
    }

    getAgentPickerWidget() {
        return this.getMatrix().find("select[name='AvailableAgentIds']").data("kendoMultiSelect");
    }

    scrollToAgent(agentId) {
        const matrix = this.getMatrix();
        const scrollContainer = matrix.find(".tmk-tax-matrix-scroll");
        const header = matrix.find(`.tmk-agent-column[data-agent-id='${agentId}']`);

        if (scrollContainer.length === 0 || header.length === 0) {
            return;
        }

        scrollContainer.animate({
            scrollLeft: scrollContainer.scrollLeft() + header.position().left - 240
        }, 150);
    }

    focusAgentBasisInput(agentId) {
        const input = this.getMatrix().find(`input[data-agent-honorary='basis'][data-agent-id='${agentId}']`);
        const widget = input.data("kendoNumericTextBox");

        if (widget) {
            widget.focus();
        } else {
            input.focus();
        }
    }

    markDetailDirty() {
        this.getDetailContainer().find("form").trigger("markDirty");
    }

    getDetailContainer() {
        return $(`#${this.detailContentContainer}`);
    }

    getMatrix() {
        return this.getDetailContainer().find("[data-role='tax-schedule-matrix']");
    }

    getResultKeyId(result) {
        if (!result) {
            return 0;
        }

        if (!isNaN(result)) {
            return Number(result);
        }

        return result.keyId || result.id || result.KeyId || result.Id || 0;
    }

    getResultMessages(result) {
        if (!result) {
            return [];
        }

        const messages = result.messages || result.Messages || [];
        return Array.isArray(messages) ? messages : [];
    }

    showServiceMessages(messages) {
        if (messages.length === 0) {
            return;
        }

        pageHelper.showSuccess(messages.map(message => kendo.htmlEncode(message)).join("<br />"));
    }
}
